import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { config } from '../../../config';
import SubscriptionService from './SubscriptionService';
import { useAuth } from '../../../context/AuthContext';
import './SubscriptionPaymentForm.css';

const stripePromise = loadStripe(config.STRIPE_PLATFORM_PUBLISHABLE_KEY);

const SubscriptionPaymentForm = ({ onSuccess, onCancel, planDetails }) => {
    const { user, token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [amount, setAmount] = useState(null);
    const [currency, setCurrency] = useState('usd');
    const [setupIntentId, setSetupIntentId] = useState(null);
    const [period, setPeriod] = useState('per year'); // fallback
    const fetchedRef = useRef(false);

    useEffect(() => {
        const createSetupIntent = async () => {
            if (!user || !token) {
                setError('User not authenticated');
                return;
            }
            if (fetchedRef.current) {
                return;
            }
            fetchedRef.current = true;

            try {
                setLoading(true);
                // Pass the selected price ID from planDetails if available
                const priceId = planDetails?.priceId || planDetails?.selectedTier?.id;
                // Pass isChangingTier flag to allow tier changes for existing subscribers
                const isChangingTier = planDetails?.isChangingTier || false;
                const result = await SubscriptionService.createPremiumPaymentIntent(user._id, token, priceId, isChangingTier);

                if (result.success) {
                    setClientSecret(result.clientSecret);
                    setAmount(result.amount);
                    setCurrency(result.currency);
                    setSetupIntentId(result.setupIntentId);
                    setPeriod(result.period || 'per year');
                } else {
                    setError(result.error || 'Failed to create setup intent');
                }
            } catch (err) {
                console.error('Error creating setup intent:', err);
                setError('Failed to create setup intent');
            } finally {
                setLoading(false);
            }
        };

        createSetupIntent();
    }, [user, token, planDetails]);

    if (loading) {
        return (
            <div className="subscription-payment-loading">
                <div className="loading-spinner"></div>
                <p>Preparing payment...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="subscription-payment-error">
                <h3>Payment Error</h3>
                <p>{error}</p>
                <button onClick={onCancel} className="btn-secondary">
                    Go Back
                </button>
            </div>
        );
    }

    if (!clientSecret) {
        return (
            <div className="subscription-payment-error">
                <h3>Payment Error</h3>
                <p>Unable to initialize payment</p>
                <button onClick={onCancel} className="btn-secondary">
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PaymentForm
                clientSecret={clientSecret}
                amount={amount}
                currency={currency}
                period={period}
                setupIntentId={setupIntentId}
                planDetails={planDetails}
                onSuccess={onSuccess}
                onCancel={onCancel}
            />
        </Elements>
    );
};

const PaymentForm = ({ clientSecret, amount, currency, period, setupIntentId, planDetails, onSuccess, onCancel }) => {
    const { user, token } = useAuth();
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements || !user || !token || processing || !setupIntentId) {
            return;
        }

        setLoading(true);
        setProcessing(true);
        setError(null);

        try {
            const cardElement = elements.getElement(CardElement);

            // Use confirmCardSetup instead of confirmCardPayment
            // This collects the payment method without charging
            const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: cardElement,
                    billing_details: {
                        name: `${user.firstname || ''} ${user.lastname || ''}`.trim(),
                        email: user.email,
                    },
                },
            });

            if (stripeError) {
                setError(stripeError.message);
                setLoading(false);
                setProcessing(false);
                return;
            }

            if (setupIntent.status === 'succeeded') {
                // Confirm the subscription on our backend
                // Backend will create subscription with the confirmed payment method
                const confirmResult = await SubscriptionService.confirmPremiumSubscription(
                    user._id,
                    setupIntent.id,
                    token
                );

                if (confirmResult.success) {
                    // Keep processing=true to prevent re-submission during success callback
                    onSuccess(confirmResult.subscription);
                } else {
                    setError(confirmResult.error || 'Failed to confirm subscription');
                    setLoading(false);
                    setProcessing(false);
                }
            } else {
                setError('Payment method setup was not successful');
                setLoading(false);
                setProcessing(false);
            }
        } catch (err) {
            console.error('Setup error:', err);
            setError('An unexpected error occurred during setup');
            setLoading(false);
            setProcessing(false);
        }
    };

    const formatAmount = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount / 100);
    };

    const isChangingTier = planDetails?.isChangingTier;

    return (
        <div className="subscription-payment-form">
            <div className="payment-header">
                <h3>{isChangingTier ? 'Change Your Subscription Plan' : 'Complete Your Premium Subscription'}</h3>
                <div className="plan-summary">
                    <h4>{planDetails?.title || 'Premium Plan'}</h4>
                    {isChangingTier && (
                        <p className="tier-change-info">
                            Changing from <strong>{planDetails.currentTierName}</strong> to <strong>{planDetails.newTierName}</strong>
                        </p>
                    )}
                    {planDetails?.selectedTier && !isChangingTier && (
                        <p className="tier-info">{planDetails.selectedTier.tierName} Billing</p>
                    )}
                    <p className="price">
                        {amount ? formatAmount(amount, currency) : planDetails?.price || '$79.00'} {period || planDetails?.period || 'per year'}
                    </p>
                    {planDetails?.selectedTier?.savingsPercent > 0 && (
                        <p className="savings-badge">
                            Save {planDetails.selectedTier.savingsPercent}% compared to monthly
                        </p>
                    )}
                    {isChangingTier && (
                        <p className="tier-change-note">
                            Your current subscription will be cancelled and a new subscription will start immediately.
                        </p>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
                <div className="card-element-container">
                    <label htmlFor="card-element">Card Information</label>
                    <CardElement
                        id="card-element"
                        options={{
                            style: {
                                base: {
                                    fontSize: '16px',
                                    color: '#424770',
                                    '::placeholder': {
                                        color: '#aab7c4',
                                    },
                                },
                                invalid: {
                                    color: '#9e2146',
                                },
                            },
                        }}
                    />
                </div>

                {error && (
                    <div className="payment-error">
                        {error}
                    </div>
                )}

                <div className="payment-actions">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="btn-secondary"
                        disabled={loading || processing}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn-primary"
                        disabled={!stripe || loading || processing}
                    >
                        {loading ? 'Processing...' : isChangingTier
                            ? `Change Plan - ${amount ? formatAmount(amount, currency) : '$79.00'}`
                            : `Subscribe for ${amount ? formatAmount(amount, currency) : '$79.00'}`}
                    </button>
                </div>
            </form>

            <div className="payment-security">
                <p>
                    <span className="security-icon">🔒</span>
                    Your payment information is secure and encrypted
                </p>
            </div>
        </div>
    );
};

export default SubscriptionPaymentForm;
